import React, { useRef, useState } from "react";
import { FileSignature, Sparkles, Loader2, Upload, Download, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, cumplimentarAnexoTender } from "@/lib/api";
import { SignAnnexButton } from "@/components/SignAnnexButton";

const ACCEPTED_EXTS = [".pdf", ".docx", ".doc", ".jpg", ".jpeg", ".png", ".webp"];

/** Inline mini-tool inside a tender's "Anexos" tab.
 * Lets the user upload a blank annex (or paste it) and fills it using the
 * tender's own pliego as context automatically. */
export function FillBlankAnnexCard({ tenderId, onFilled }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [instructions, setInstructions] = useState("");
    const [pastedText, setPastedText] = useState("");
    const [file, setFile] = useState(null);
    const [lastResult, setLastResult] = useState(null);
    const fileRef = useRef(null);

    const reset = () => {
        setTitle("");
        setInstructions("");
        setPastedText("");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file && !pastedText.trim()) {
            toast.error("Sube un archivo o pega el texto del anexo");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            if (title.trim()) fd.append("title", title.trim());
            if (instructions.trim()) fd.append("instructions", instructions.trim());
            if (pastedText.trim()) fd.append("pasted_text", pastedText.trim());
            if (file) fd.append("file", file);
            const result = await cumplimentarAnexoTender(tenderId, fd);
            toast.success("Anexo cumplimentado por la IA ✨");
            setLastResult(result);
            reset();
            if (onFilled) onFilled(result);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "No se pudo cumplimentar. Inténtalo de nuevo."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const onDownload = async (id, fmt, fileTitle) => {
        try {
            const res = await api.get(`/anexos/cumplimentados/${id}/download/${fmt}`, {
                responseType: "blob",
            });
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (fileTitle || "anexo").replace(/[^A-Za-z0-9 _.-]/g, "_");
            a.download = `${safe}.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Descarga falló");
        }
    };

    if (!open) {
        return (
            <div
                className="rounded-2xl border-2 border-dashed border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/60 p-5 hover:border-amber-300 transition-all"
                data-testid="fill-blank-annex-card"
            >
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-pop shrink-0">
                        <FileSignature size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-lg font-black text-ink-950">
                            ¿Tienes un anexo en blanco que rellenar?
                        </div>
                        <div className="text-sm text-ink-500 font-medium">
                            Súbelo (PDF, Word, foto o texto) y la IA lo cumplimenta usando los
                            datos del pliego de esta licitación.
                        </div>
                    </div>
                    <Button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-11 px-5 font-bold shadow-pop gap-2"
                        data-testid="open-fill-blank-annex"
                    >
                        <Sparkles size={14} />
                        Cumplimentar anexo
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/40 to-orange-50/40 p-5 space-y-4"
            data-testid="fill-blank-annex-form"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileSignature className="text-amber-600" size={20} />
                    <h3 className="font-display text-lg font-black">
                        Cumplimentar anexo de esta licitación
                    </h3>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                        setOpen(false);
                        reset();
                        setLastResult(null);
                    }}
                    className="h-8 w-8 p-0 rounded-full"
                    data-testid="close-fill-blank-annex"
                >
                    <X size={16} />
                </Button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            Título
                        </Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ej: Anexo I — Declaración responsable"
                            className="mt-1 h-10 rounded-xl border-ink-200 text-sm"
                            data-testid="fill-blank-title"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            Archivo del anexo
                        </Label>
                        <input
                            ref={fileRef}
                            type="file"
                            accept={ACCEPTED_EXTS.join(",")}
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="mt-1 block w-full text-sm font-medium file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:font-bold file:bg-amber-100 file:text-amber-700 hover:file:bg-amber-200 text-ink-500"
                            data-testid="fill-blank-file"
                        />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                        …o pega el texto del anexo
                    </Label>
                    <Textarea
                        value={pastedText}
                        onChange={(e) => setPastedText(e.target.value)}
                        placeholder="Pega aquí el texto literal del anexo en blanco."
                        className="mt-1 min-h-[80px] rounded-xl border-ink-200 font-mono text-xs"
                        data-testid="fill-blank-pasted"
                    />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                        Instrucciones para la IA (opcional)
                    </Label>
                    <Textarea
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        placeholder='Ej: "Empresa española", "Ofertamos 124.500 € IVA excluido", "Plazo 24 meses"'
                        className="mt-1 min-h-[60px] rounded-xl border-ink-200 text-sm"
                        data-testid="fill-blank-instructions"
                    />
                </div>
                <div className="flex items-center justify-end gap-2">
                    <Button
                        type="submit"
                        disabled={submitting || (!file && !pastedText.trim())}
                        className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-11 px-5 font-bold shadow-pop gap-2 disabled:opacity-50"
                        data-testid="fill-blank-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Cumplimentando…
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} />
                                Cumplimentar con IA
                            </>
                        )}
                    </Button>
                </div>
            </form>

            {lastResult && (
                <div
                    className="mt-2 rounded-xl bg-white border border-emerald-200 p-4 flex items-center gap-3"
                    data-testid="fill-blank-success"
                >
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <FileSignature size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-ink-950 truncate">
                            {lastResult.title}
                        </div>
                        <div className="text-xs text-emerald-700 font-bold">
                            ¡Listo! Descárgalo en PDF o Word.
                        </div>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            onDownload(lastResult.id, "pdf", lastResult.title)
                        }
                        className="rounded-full h-9 px-3 border-ink-200 font-bold text-xs gap-1.5"
                        data-testid="fill-blank-dl-pdf"
                    >
                        <Download size={12} /> PDF
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            onDownload(lastResult.id, "docx", lastResult.title)
                        }
                        className="rounded-full h-9 px-3 border-ink-200 font-bold text-xs gap-1.5"
                        data-testid="fill-blank-dl-docx"
                    >
                        <Download size={12} /> Word
                    </Button>
                    <SignAnnexButton
                        annexId={lastResult.id}
                        isSigned={Boolean(lastResult.signed_pdf_storage_path)}
                        signerDn={lastResult.signer_dn}
                        compact
                        onSigned={(res) =>
                            setLastResult((prev) =>
                                prev
                                    ? {
                                          ...prev,
                                          signed_pdf_storage_path: "set",
                                          signed_at: res.signed_at,
                                          signer_dn: res.signer_dn,
                                      }
                                    : prev
                            )
                        }
                    />
                </div>
            )}
        </div>
    );
}
